import { Link, useNavigate, useParams } from "react-router-dom";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import SectionHeader from "@/components/feature/SectionHeader";
import ShareButton from "@/components/feature/ShareButton";
import { getNewsById, getRelatedNews } from "@/lib/news";
import { getMediaById, categoryLabel } from "@/lib/media";

export default function NewsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const news = getNewsById(id);

  if (!news) {
    return (
      <div className="w-full px-4 md:px-8 py-20">
        <PageMeta title="资讯不存在-蜜桃成熟" />
        <EmptyState
          icon="ri-newspaper-line"
          title="没有找到这条资讯"
          description="它可能已被移除，返回资讯列表继续浏览吧。"
          actionLabel="返回资讯列表"
          onAction={() => navigate("/news")}
        />
      </div>
    );
  }

  const related = getRelatedNews(news, 4);
  const media = getMediaById(news.relatedMediaId);

  return (
    <>
      <PageMeta
        title={`${news.title} - 蜜桃成熟`}
        description={news.summary}
      />

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "影视资讯", to: "/news" },
            { label: news.title },
          ]}
        />

        <article className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 min-w-0">
            <span className="inline-block px-3 py-1 rounded-full text-xs bg-accent-100 text-accent-800 mb-4">
              {news.category}
            </span>
            <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 leading-snug">
              {news.title}
            </h1>
            <div className="flex items-center flex-wrap gap-4 text-sm text-foreground-400 mt-4 pb-6 border-b border-background-200">
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </span>
                {news.author}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                {news.publishTime}
              </span>
              <div className="ml-auto">
                <ShareButton title={news.title} />
              </div>
            </div>

            <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden bg-background-100 my-6">
              <img
                src={news.image}
                alt={news.title}
                title={`${news.title} 配图`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="space-y-5">
              {news.content.map((paragraph, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
                  {paragraph}
                </p>
              ))}
            </div>

            {media && (
              <Link
                to={`/detail/${media.id}`}
                className="group flex items-center gap-4 mt-8 p-4 rounded-lg border border-background-200 bg-primary-50 hover:border-primary-300 transition-colors"
              >
                <div className="w-16 h-24 shrink-0 rounded-md overflow-hidden bg-background-100">
                  <img
                    src={media.cover}
                    alt={media.title}
                    title={`${media.title} 详情`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-primary-700 mb-1">相关作品</p>
                  <h3 className="text-base font-medium text-foreground-950 group-hover:text-primary-700 transition-colors">
                    《{media.title}》
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">
                    {categoryLabel(media.type)} · {media.year} · {media.region}
                  </p>
                </div>
                <span className="ml-auto w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shrink-0">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            )}

            <div className="mt-10">
              <button
                type="button"
                onClick={() => navigate("/news")}
                className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 whitespace-nowrap transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
                返回资讯列表
              </button>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <SectionHeader title="相关推荐" accent />
            <div className="flex flex-col gap-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/detail/${item.id}`}
                  className="group flex gap-3 p-3 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                >
                  <div className="w-20 h-16 shrink-0 rounded-md overflow-hidden bg-background-100">
                    <img
                      src={item.image}
                      alt={item.title}
                      title={`${item.title} 资讯`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-2">{item.publishTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        </article>
      </div>
    </>
  );
}